"use client";

import { useState } from "react";
import {
  UneligibleTradeReason,
  StreakSummaryResponse,
  StreakSummaryResponseV2,
  AUTOMATIC_STREAK_STATUS,
} from "@cede/types";
import {
  DISCORD_INVITE_URL,
  formatCountdown,
  formatDefiAddress,
  isEmptyCountdown,
  getCexSignUpUrl,
  getExchangeLabel,
} from "@cede/utils";
import { EligibilityRequirementsList } from "./EligibilityRequirementsList";
import { AnimatedCedeLogo } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useCountdown } from "@/hooks";
import { useDevice } from "@/hooks";
import { useEligibilityRequirements } from "@/hooks/campaign/useEligibilityRequirements";
import { usePerpsPnlOptIn } from "@/hooks/campaign/usePerpsPnlOptIn";
import { useHoldCampaign } from "@/hooks/hold/useHoldCampaign";
import { MERKL_EVM_WALLET_REQUIRED_MESSAGE, useJoinHoldCampaign } from "@/hooks/hold/useJoinHoldCampaign";
import { useHubUser } from "@/hooks/user/useHubUser";
import { cn } from "@/lib/utils";
import { useConnectStore, FLOW_TYPES, STEPS } from "@/store/connectStore";
import { isEVMAddress } from "@/utils/address";
import { getStatusConfig, getStopReasonLabel, isManualBlockerAllowingAutopilot } from "@/utils/automatic-streak";
import { CheckCircle, Clock, Pause, Ban, AlertTriangle, Loader2, Bot, ChevronDown, Info } from "lucide-react";
import { useRouter } from "next/navigation";
import { enqueueSnackbar } from "notistack";

type UnifiedStreakSummary = StreakSummaryResponse | StreakSummaryResponseV2;

function isV2StreakSummary(streak: UnifiedStreakSummary): streak is StreakSummaryResponseV2 {
  return "version" in streak && streak.version === "V2";
}

type BlockedAccessMessageProps = {
  invalidAccessReason: UneligibleTradeReason;
  defiAddresses: string[];
  nextAccessDate: Date | undefined;
  hteCampaignId?: string;
  hteCampaignExchangeIds?: string[];
  hteCampaignName?: string;
  onWalletDisconnect?: () => void;
  onResetForm?: () => void;
  walletAgeReason?: string | null;
  /** Most recent autopilot streak for this campaign (if any) */
  recentStreak?: UnifiedStreakSummary;
  /** Callback to switch to autopilot tab */
  onSwitchToAutopilot?: () => void;
  /** Whether autopilot is available for this campaign */
  isAutopilotAvailable?: boolean;
  /** Exchange IDs that require Cede referral (for NOT_AFFILIATED_REFERRAL reason) */
  referralOnlyExchangeIds?: string[];
  /** Campaign name (used for perps PnL opt-in) */
  campaignName?: string;
  /** Exchange account ID (used for perps PnL opt-in) */
  exchangeAccountId?: string;
  /** Campaign's target exchange IDs (used to name the exchange in blocked-access messages). */
  blockedExchangeIds?: string[];
};

// Status icons for autopilot card
const STATUS_ICONS: Record<string, React.ReactNode> = {
  [AUTOMATIC_STREAK_STATUS.PENDING]: <Clock className="w-4 h-4" />,
  [AUTOMATIC_STREAK_STATUS.IN_PROGRESS]: <Loader2 className="w-4 h-4 animate-spin" />,
  [AUTOMATIC_STREAK_STATUS.COMPLETED]: <CheckCircle className="w-4 h-4" />,
  [AUTOMATIC_STREAK_STATUS.STOPPED]: <Pause className="w-4 h-4" />,
  [AUTOMATIC_STREAK_STATUS.CANCELLED]: <Ban className="w-4 h-4" />,
};

export const BlockedAccessMessage = ({
  invalidAccessReason,
  defiAddresses,
  nextAccessDate,
  hteCampaignId,
  hteCampaignName,
  hteCampaignExchangeIds,
  onWalletDisconnect,
  onResetForm,
  walletAgeReason,
  recentStreak,
  onSwitchToAutopilot,
  isAutopilotAvailable,
  referralOnlyExchangeIds,
  campaignName,
  exchangeAccountId,
  blockedExchangeIds,
}: BlockedAccessMessageProps) => {
  const { isMobile } = useDevice();
  const router = useRouter();
  const nextWindowCountdown = useCountdown(nextAccessDate);
  const { eligibilityRequirements, handleScrollToDescription } = useEligibilityRequirements({ invalidAccessReason });

  // Join campaign functionality for hold campaigns
  const { mutate: joinCampaign, isPending: isJoining } = useJoinHoldCampaign();
  const { campaign: hteCampaign } = useHoldCampaign(hteCampaignName);
  const { hubUser } = useHubUser();
  const isMerklEvmWalletMissing =
    hteCampaign?.rewardDistribution === "merkl" && !(hubUser?.defiAddress && isEVMAddress(hubUser.defiAddress));

  // Opt-in for perps PnL campaigns
  const { mutate: optInPerpsPnl, isPending: isOptingIn } = usePerpsPnlOptIn();

  // Perps PnL opt-in checkboxes
  const [noOpenPositions, setNoOpenPositions] = useState(false);
  const [approvedRules, setApprovedRules] = useState(false);
  const isPerpsPnlOptInReady = noOpenPositions && approvedRules;

  // Connect exchange account functionality
  const { setOpenConnectModal, setFlowType, setExchangeIds, setActiveStep } = useConnectStore();

  const handleJoinCampaign = () => {
    if (hteCampaignId) {
      joinCampaign({ campaignId: hteCampaignId, campaignName: hteCampaignName || "" });
    }
  };

  const handlePerpsPnlOptIn = () => {
    if (!campaignName) {
      enqueueSnackbar("Missing campaign context — please refresh and try again.", { variant: "error" });
      return;
    }
    if (!exchangeAccountId) {
      enqueueSnackbar("Connect your exchange account before opting in.", { variant: "error" });
      return;
    }
    optInPerpsPnl({ campaignName, exchangeAccountId });
  };

  const handleConnectExchange = () => {
    if (hteCampaignExchangeIds && hteCampaignExchangeIds.length > 0) {
      setExchangeIds(hteCampaignExchangeIds);
    } else {
      setExchangeIds([]);
    }
    setFlowType(FLOW_TYPES.READY_TO_USE);
    setActiveStep(STEPS.ChooseCEX);
    setOpenConnectModal(true);
  };

  const handleBackToCampaigns = () => {
    // Hold campaigns live under /hold/*; everything else returns to the campaigns list.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/hold/")) {
      router.push(ROUTES.HOLD);
      return;
    }
    router.push(ROUTES.HOME);
  };

  const handleOpenReferralLink = () => {
    const exchangeId = referralOnlyExchangeIds?.[0];
    if (exchangeId) {
      const url = getCexSignUpUrl(exchangeId);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    }
  };

  const blockedExchangeName = blockedExchangeIds?.[0] ? getExchangeLabel(blockedExchangeIds[0]) : undefined;

  const isRestricted = invalidAccessReason === UneligibleTradeReason.RESTRICTED_PARTICIPANTS;
  const logoSize = isRestricted ? 60 : 90;

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center min-h-full p-[32px]",
        isRestricted ? "gap-6" : "gap-[48px]",
      )}
    >
      <AnimatedCedeLogo
        width={logoSize}
        height={logoSize}
        shadowColor={invalidAccessReason === UneligibleTradeReason.USER_BANNED ? "red" : "blue"}
      />

      {/* Main message */}
      <h5 className="text-center h5">
        {invalidAccessReason === UneligibleTradeReason.USER_BANNED && (
          <span className="text-cede-error">You are blocked.</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.LIMIT_REACHED && (
          <span>All paid spots are currently taken!</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.MAX_TOTAL_VOLUME_BY_EPOCH_REACHED && (
          <span>The maximum volume for this epoch has been reached!</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.MAX_USER_VOLUME_BY_EPOCH_REACHED && (
          <span>You have reached your maximum trading volume for this epoch!</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.COOLDOWN_VOLUME_REACHED && (
          <span>You have reached the maximum trading volume!</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.MAX_DEFI_ADDRESSES_BY_EXCHANGE_USER_ID && (
          <>
            <span>Different </span>
            <span className="text-cede-primary">wallet address detected</span>
          </>
        )}
        {invalidAccessReason === UneligibleTradeReason.EXCHANGE_ACCOUNT_ALREADY_TRADED && (
          <>
            <span>You have already traded with this </span>
            <span className="text-cede-primary">exchange account</span>
          </>
        )}
        {invalidAccessReason === UneligibleTradeReason.ALREADY_TRADED && (
          <>
            <span>You have already </span>
            <span className="text-cede-primary">traded</span>
          </>
        )}
        {invalidAccessReason === UneligibleTradeReason.ALREADY_TRADED_ON_EXCHANGE && (
          <>
            <span>You have already </span>
            <span className="text-cede-primary">traded on this exchange</span>
          </>
        )}
        {invalidAccessReason === UneligibleTradeReason.CAMPAIGN_NOT_STARTED && (
          <span>Campaign has not started yet</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.CAMPAIGN_PAUSED && (
          <span className="text-cede-warning">Campaign is currently paused</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.CAMPAIGN_PAST && <span>Campaign has ended</span>}
        {invalidAccessReason === UneligibleTradeReason.ALREADY_PARTICIPATED && (
          <>
            <span>You have already </span>
            <span className="text-cede-primary">participated</span>
            <span> in this campaign</span>
          </>
        )}
        {invalidAccessReason === UneligibleTradeReason.CAMPAIGN_OPT_IN_REQUIRED && (
          <span>Join this campaign to start trading</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.HTE_NOT_JOINED && (
          <span>Join this campaign to start earning</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.HTE_OPTED_OUT && (
          <span className="text-cede-warning">You have opted out of this campaign</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.HTE_NO_SPOTS_AVAILABLE && (
          <span className="text-cede-warning">All spots are currently taken!</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.HTE_NO_EXCHANGE_ACCOUNT && (
          <span>Connect a supported exchange account to opt-in to this campaign</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.RESTRICTED_PARTICIPANTS && (
          <span>This is an exclusive campaign</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.WALLET_TOO_NEW && (
          <>
            <span>Your wallet is </span>
            <span className="text-cede-warning">too new</span>
            <span> for this campaign</span>
          </>
        )}
        {invalidAccessReason === UneligibleTradeReason.NOT_AFFILIATED_REFERRAL && (
          <span>You are not eligible for this campaign</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.PERPS_PNL_OPT_IN_REQUIRED && (
          <span>Opt in to start trading</span>
        )}
        {invalidAccessReason === UneligibleTradeReason.EXTERNAL_ORDER_DETECTED && (
          <span className="text-cede-warning">External trading detected</span>
        )}
        {/* Default fallback */}
        {![
          UneligibleTradeReason.USER_BANNED,
          UneligibleTradeReason.LIMIT_REACHED,
          UneligibleTradeReason.MAX_TOTAL_VOLUME_BY_EPOCH_REACHED,
          UneligibleTradeReason.MAX_USER_VOLUME_BY_EPOCH_REACHED,
          UneligibleTradeReason.COOLDOWN_VOLUME_REACHED,
          UneligibleTradeReason.MAX_DEFI_ADDRESSES_BY_EXCHANGE_USER_ID,
          UneligibleTradeReason.EXCHANGE_ACCOUNT_ALREADY_TRADED,
          UneligibleTradeReason.ALREADY_TRADED,
          UneligibleTradeReason.ALREADY_TRADED_ON_EXCHANGE,
          UneligibleTradeReason.CAMPAIGN_NOT_STARTED,
          UneligibleTradeReason.CAMPAIGN_PAUSED,
          UneligibleTradeReason.CAMPAIGN_PAST,
          UneligibleTradeReason.ALREADY_PARTICIPATED,
          UneligibleTradeReason.CAMPAIGN_OPT_IN_REQUIRED,
          UneligibleTradeReason.HTE_NOT_JOINED,
          UneligibleTradeReason.HTE_OPTED_OUT,
          UneligibleTradeReason.HTE_NO_SPOTS_AVAILABLE,
          UneligibleTradeReason.HTE_NO_EXCHANGE_ACCOUNT,
          UneligibleTradeReason.RESTRICTED_PARTICIPANTS,
          UneligibleTradeReason.WALLET_TOO_NEW,
          UneligibleTradeReason.NOT_AFFILIATED_REFERRAL,
          UneligibleTradeReason.PERPS_PNL_OPT_IN_REQUIRED,
          UneligibleTradeReason.EXTERNAL_ORDER_DETECTED,
        ].includes(invalidAccessReason) && <span className="text-cede-warning">Access restricted</span>}
      </h5>

      {/* More information */}
      {invalidAccessReason === UneligibleTradeReason.MAX_DEFI_ADDRESSES_BY_EXCHANGE_USER_ID && (
        <p className="body1 text-center">
          You can only use one wallet address per exchange account. Please use{" "}
          <span className="text-cede-primary">
            {defiAddresses[0] ? formatDefiAddress(defiAddresses[0]) : "the previous address"}
          </span>{" "}
          or disconnect your wallet to use a different address.
        </p>
      )}

      {invalidAccessReason === UneligibleTradeReason.USER_BANNED && (
        <p className="body1 text-center">
          If you believe this is an error, please contact us on{" "}
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cede-primary !underline"
          >
            Discord
          </a>
          .
        </p>
      )}

      {invalidAccessReason === UneligibleTradeReason.HTE_NO_SPOTS_AVAILABLE && (
        <p className="body1 text-center">
          This fixed spot campaign has reached its participant limit. You can try again in future epochs or participate
          in other campaigns in the meantime.
        </p>
      )}

      {invalidAccessReason === UneligibleTradeReason.ALREADY_TRADED && (
        <p className="body1 text-center">This epoch is accessible only to first time traders.</p>
      )}

      {invalidAccessReason === UneligibleTradeReason.ALREADY_TRADED_ON_EXCHANGE && (
        <p className="body1 text-center">
          This campaign is only available to users who have never traded on {blockedExchangeName ?? "this exchange"} via
          Cede Hub.
        </p>
      )}

      {invalidAccessReason === UneligibleTradeReason.WALLET_TOO_NEW && walletAgeReason && (
        <p className="body1 text-center">{walletAgeReason}</p>
      )}
      {invalidAccessReason === UneligibleTradeReason.WALLET_TOO_NEW && !walletAgeReason && (
        <p className="body1 text-center">Wallet must be at least 2 weeks old to participate.</p>
      )}

      {invalidAccessReason === UneligibleTradeReason.RESTRICTED_PARTICIPANTS &&
        (eligibilityRequirements.length > 0 ? (
          <EligibilityRequirementsList requirements={eligibilityRequirements} />
        ) : (
          <p className="body1 text-center text-cede-text-secondary">See more information in the description below</p>
        ))}

      {invalidAccessReason === UneligibleTradeReason.NOT_AFFILIATED_REFERRAL && (
        <p className="body1 text-justify">
          To participate in this campaign, your exchange account must be referred by Cede Hub. To join, please create an
          account using the button below.
        </p>
      )}

      {invalidAccessReason === UneligibleTradeReason.PERPS_PNL_OPT_IN_REQUIRED &&
        (exchangeAccountId ? (
          <div className="flex flex-col gap-3 w-full max-w-[300px]">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={noOpenPositions}
                onCheckedChange={setNoOpenPositions}
                id="no-open-positions"
                className="mt-0.5 focus-within:ring-0 focus-within:ring-offset-0"
              />
              <span className="body2 text-cede-text-secondary select-none">
                I have no opened position on the Exchange
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={approvedRules}
                onCheckedChange={setApprovedRules}
                id="approved-rules"
                className="mt-0.5 focus-within:ring-0 focus-within:ring-offset-0"
              />
              <span className="body2 text-cede-text-secondary select-none">
                Read and approve the campaign rules{" "}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex align-middle"
                      aria-label="More info about campaign rules"
                    >
                      <Info className="w-4 h-4 text-cede-text-secondary hover:text-cede-text-primary transition-colors" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[260px] text-center">
                    Once opted in, all trades on the campaign pair must go through this platform. If external trades are
                    detected,{" "}
                    <span className="font-bold">you will no longer be able to trade for the current epoch</span>.
                  </TooltipContent>
                </Tooltip>
              </span>
            </label>
          </div>
        ) : (
          <p className="body2 text-cede-text-secondary text-center max-w-[300px]">
            Connect your exchange account to opt in to this campaign.
          </p>
        ))}

      {invalidAccessReason === UneligibleTradeReason.EXTERNAL_ORDER_DETECTED && (
        <p className="body1 text-center">
          You placed a trade outside of the platform. You are not eligible to trade for this epoch.
        </p>
      )}

      {(invalidAccessReason === UneligibleTradeReason.LIMIT_REACHED ||
        invalidAccessReason === UneligibleTradeReason.MAX_TOTAL_VOLUME_BY_EPOCH_REACHED ||
        invalidAccessReason === UneligibleTradeReason.MAX_USER_VOLUME_BY_EPOCH_REACHED ||
        invalidAccessReason === UneligibleTradeReason.COOLDOWN_VOLUME_REACHED) && (
        <div className="text-center flex flex-col items-center gap-4">
          {nextWindowCountdown &&
          isEmptyCountdown(nextWindowCountdown) &&
          invalidAccessReason !== UneligibleTradeReason.COOLDOWN_VOLUME_REACHED ? (
            "Sorry, it's the last epoch of the campaign. Come back next time!"
          ) : (
            <div className="flex flex-col gap-1 animate-in fade-in duration-1000">
              <p className="text-left text-cede-primary-dark">Next trading session starts in</p>
              <p className="font-bold h3">{nextWindowCountdown ? formatCountdown(nextWindowCountdown, "clock") : ""}</p>
            </div>
          )}
        </div>
      )}

      {/* Autopilot Status Card - shown when user has a recent streak */}
      {recentStreak && (
        <div
          className={cn(
            "w-full max-w-[300px] p-3 rounded-lg border border-cede-border-container bg-cede-bg-component/80 animate-in fade-in duration-500",
          )}
        >
          <div className="flex items-center gap-2 mb-2">
            {(() => {
              const statusConfig = getStatusConfig(recentStreak.status);
              const statusIcon = STATUS_ICONS[recentStreak.status] ?? <AlertTriangle className="w-4 h-4" />;
              return (
                <>
                  <div className={cn("p-1.5 rounded-md", statusConfig.bgColor, statusConfig.color)}>{statusIcon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-cede-text-primary truncate">
                        Autopilot: {recentStreak.pairSymbol}
                      </span>
                      <span className={cn("text-xs", statusConfig.color)}>{statusConfig.label}</span>
                    </div>
                    <div className="text-xs text-cede-text-secondary">
                      {isV2StreakSummary(recentStreak) ? (
                        <>
                          ${parseFloat(recentStreak.totalVolumeTraded).toFixed(2)} / $
                          {parseFloat(recentStreak.totalTargetVolume).toFixed(2)}
                          {(recentStreak.skippedCycles ?? 0) > 0 && (
                            <span className="text-cede-warning ml-1">({recentStreak.skippedCycles} skipped)</span>
                          )}
                        </>
                      ) : (
                        <>
                          {recentStreak.completedTrades}/{recentStreak.totalTrades} cycles
                          {(recentStreak.skippedCycles ?? 0) > 0 && (
                            <span className="text-cede-warning ml-1">({recentStreak.skippedCycles} skipped)</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          {recentStreak.stopReason && (
            <div className="flex items-start gap-1.5 text-xs text-cede-error bg-cede-error/10 rounded p-2">
              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              <span>{getStopReasonLabel(recentStreak.stopReason)}</span>
            </div>
          )}
        </div>
      )}

      {invalidAccessReason === UneligibleTradeReason.CAMPAIGN_OPT_IN_REQUIRED && (
        <span className="body2 text-cede-text-secondary">
          Check the information on the {isMobile ? "bottom" : "right"}
        </span>
      )}

      {/* Action buttons */}
      <div
        className={cn(
          "flex flex-col items-stretch gap-[var(--spacing-gutter)]",
          isRestricted ? "mt-2 w-full max-w-[340px]" : "mt-8 w-[260px]",
        )}
      >
        {(invalidAccessReason === UneligibleTradeReason.CAMPAIGN_OPT_IN_REQUIRED ||
          invalidAccessReason === UneligibleTradeReason.HTE_NOT_JOINED ||
          invalidAccessReason === UneligibleTradeReason.HTE_OPTED_OUT) &&
          hteCampaignId && (
            <>
              <Button
                className="w-full"
                onClick={handleJoinCampaign}
                variant="primary"
                size="large"
                disabled={isJoining || isMerklEvmWalletMissing}
                data-testid="join-campaign-btn"
              >
                {isJoining
                  ? "Joining..."
                  : invalidAccessReason === UneligibleTradeReason.HTE_OPTED_OUT
                    ? "Rejoin Campaign"
                    : "Join Campaign"}
              </Button>
              {isMerklEvmWalletMissing && (
                <p className="body2 text-center text-cede-warning">{MERKL_EVM_WALLET_REQUIRED_MESSAGE}</p>
              )}
            </>
          )}
        {invalidAccessReason === UneligibleTradeReason.PERPS_PNL_OPT_IN_REQUIRED &&
          campaignName &&
          (exchangeAccountId ? (
            <Button
              className="w-full transition-all duration-300"
              onClick={handlePerpsPnlOptIn}
              variant="primary"
              size="large"
              disabled={!isPerpsPnlOptInReady || isOptingIn}
              data-testid="perps-pnl-opt-in-btn"
            >
              {isOptingIn ? (
                <span className="flex items-center justify-center gap-2 animate-in fade-in duration-200">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Opting in...
                </span>
              ) : (
                "Opt in to the campaign"
              )}
            </Button>
          ) : (
            <Button
              className="w-full"
              onClick={handleConnectExchange}
              variant="primary"
              size="large"
              data-testid="perps-pnl-connect-exchange-btn"
            >
              Connect Exchange
            </Button>
          ))}
        {invalidAccessReason === UneligibleTradeReason.HTE_NO_EXCHANGE_ACCOUNT && (
          <Button
            className="w-full"
            onClick={handleConnectExchange}
            variant="primary"
            size="large"
            data-testid="connect-exchange-btn"
          >
            Connect CEX Account
          </Button>
        )}
        {invalidAccessReason === UneligibleTradeReason.MAX_DEFI_ADDRESSES_BY_EXCHANGE_USER_ID && (
          <Button
            className="w-full"
            onClick={() => {
              onWalletDisconnect?.();
              onResetForm?.();
            }}
            variant="primary"
            size="large"
            data-testid="back-btn"
          >
            Disconnect Wallet
          </Button>
        )}
        {/* Manage Autopilot button for blockers that still allow autopilot (volume saturation) */}
        {isAutopilotAvailable && onSwitchToAutopilot && isManualBlockerAllowingAutopilot(invalidAccessReason) && (
          <Button
            className="w-full"
            onClick={onSwitchToAutopilot}
            variant="primary"
            size="large"
            data-testid="manage-autopilot-btn"
          >
            <Bot className="w-4 h-4 mr-2" />
            Manage Autopilot
          </Button>
        )}
        {invalidAccessReason === UneligibleTradeReason.NOT_AFFILIATED_REFERRAL &&
          referralOnlyExchangeIds &&
          referralOnlyExchangeIds.length > 0 && (
            <Button
              className="w-full"
              onClick={handleOpenReferralLink}
              variant="primary"
              size="large"
              data-testid="create-account-btn"
            >
              Create an account
            </Button>
          )}
        {invalidAccessReason === UneligibleTradeReason.RESTRICTED_PARTICIPANTS && (
          <>
            <Button
              className="w-full"
              onClick={handleScrollToDescription}
              variant="primary"
              size="medium"
              data-testid="view-requirements-btn"
            >
              View full description
              <ChevronDown className="w-4 h-4 ml-1.5" />
            </Button>
            <Button
              className="w-full"
              onClick={handleBackToCampaigns}
              variant="secondary"
              size="medium"
              data-testid="back-btn"
            >
              Back to campaigns
            </Button>
          </>
        )}
        {(UneligibleTradeReason.EXCHANGE_ACCOUNT_ALREADY_TRADED === invalidAccessReason ||
          UneligibleTradeReason.ALREADY_TRADED === invalidAccessReason ||
          UneligibleTradeReason.ALREADY_TRADED_ON_EXCHANGE === invalidAccessReason ||
          UneligibleTradeReason.LIMIT_REACHED === invalidAccessReason ||
          UneligibleTradeReason.MAX_TOTAL_VOLUME_BY_EPOCH_REACHED === invalidAccessReason ||
          UneligibleTradeReason.MAX_USER_VOLUME_BY_EPOCH_REACHED === invalidAccessReason ||
          UneligibleTradeReason.COOLDOWN_VOLUME_REACHED === invalidAccessReason ||
          UneligibleTradeReason.CAMPAIGN_NOT_STARTED === invalidAccessReason ||
          UneligibleTradeReason.CAMPAIGN_PAST === invalidAccessReason ||
          UneligibleTradeReason.CAMPAIGN_PAUSED === invalidAccessReason ||
          UneligibleTradeReason.ALREADY_PARTICIPATED === invalidAccessReason ||
          UneligibleTradeReason.WALLET_TOO_NEW === invalidAccessReason ||
          UneligibleTradeReason.EXTERNAL_ORDER_DETECTED === invalidAccessReason) && (
          <Button
            className="w-full"
            onClick={handleBackToCampaigns}
            variant={isAutopilotAvailable && onSwitchToAutopilot ? "secondary" : "primary"}
            size="large"
            data-testid="back-btn"
          >
            Back to campaigns
          </Button>
        )}
      </div>
    </div>
  );
};
